import api from '../services/api.js';
import { createSyncEngine } from './syncCore.js';
import { currentOps, engineStore, ensureReady, getMeta, setMeta } from './fieldQueue.js';
import { pendingOf } from './queueCore.js';

/**
 * Browser side of field sync: replays the shared offline queue through POST /api/field-sync when the device comes back
 * online, every few seconds while actions are waiting, when the app returns to the foreground, when the service
 * worker's Background Sync fires, and on demand ("Sync now").
 */

const engine = createSyncEngine({
  store: engineStore,
  send: (operations) => api.request('/field-sync', { method: 'POST', body: { operations } }),
  isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine),
});

const listeners = new Set();
let lastResult = null;
const state = { syncing: false };

function emit(event) {
  listeners.forEach((fn) => fn(event));
}

/** Subscribe to sync events: { kind: 'start' } | { kind: 'done', result }. Returns an unsubscribe function. */
export function onSync(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const isSyncing = () => state.syncing;
export const lastSyncResult = () => lastResult;

/** Replays the queue now. Resolves with the run summary (applied / conflicts / rejected / stopped). */
export async function syncNow() {
  await ensureReady();
  state.syncing = true;
  emit({ kind: 'start' });
  let result;
  try {
    result = await engine.sync();
  } catch (e) {
    result = { applied: 0, duplicates: 0, conflicts: 0, rejected: 0, results: [], stopped: true, error: e.message };
  } finally {
    state.syncing = false;
  }
  await engineStore.done();
  // An attempt made while offline is not a sync: the "last sync" time only moves when the server was reached.
  if (result.results.length || !result.offline) {
    lastResult = result;
    await setMeta('lastSync', { at: result.syncedAt || new Date().toISOString(), applied: result.applied, duplicates: result.duplicates, conflicts: result.conflicts, rejected: result.rejected, error: result.error || null });
  }
  emit({ kind: 'done', result });
  return result;
}

export const getLastSync = () => getMeta('lastSync');

let started = false;
/** Starts automatic sync (idempotent). Called once by the app shell. */
export function startAutoSync() {
  if (started || typeof window === 'undefined') return;
  started = true;
  const kick = () => {
    if (navigator.onLine && pendingOf(currentOps()).length && !engine.busy) syncNow();
  };
  window.addEventListener('online', () => syncNow());
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && kick());
  setInterval(kick, 5000);
  // Background Sync: the service worker cannot read the session, so it asks an open page to run the sync.
  navigator.serviceWorker?.addEventListener?.('message', (e) => e.data?.type === 'assetos-sync' && syncNow());
  ensureReady().then(kick);
}

/** Asks the service worker to wake us for a sync when connectivity returns (Background Sync, where supported). */
export async function requestBackgroundSync() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    await reg?.sync?.register('assetos-field-sync');
  } catch {
    /* not supported: the online event and the timer cover it */
  }
}
